import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Building2, Boxes, RefreshCw, WalletCards } from 'lucide-react';
import { api } from '../lib/apiClient';

const money = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FinancialArchiveAssetsBridge(){
  const location = useLocation();
  const [host, setHost] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async()=>{
    setLoading(true);
    try{
      const r = await api.get('/finance/company-assets');
      setData(r.data || {});
    }catch{
      setData(null);
    }finally{
      setLoading(false);
    }
  },[]);

  useEffect(()=>{
    if(location.pathname !== '/finansal-arsiv'){
      setHost(null);
      return;
    }
    let cancelled=false;
    let timer=null;
    const attach=()=>{
      if(cancelled)return;
      const label=[...document.querySelectorAll('div')].find(x=>String(x.textContent||'').trim()==='Finansal PDF / Excel Raporlama');
      const section=label?.closest('section');
      if(!section?.parentElement){timer=window.setTimeout(attach,80);return;}
      let node=document.getElementById('financial-archive-company-assets-host');
      if(!node){
        node=document.createElement('div');
        node.id='financial-archive-company-assets-host';
        node.className='w-full';
        section.insertAdjacentElement('afterend',node);
      }else if(node.previousElementSibling!==section){
        section.insertAdjacentElement('afterend',node);
      }
      setHost(node);
    };
    attach();
    return()=>{
      cancelled=true;
      window.clearTimeout(timer);
      document.getElementById('financial-archive-company-assets-host')?.remove();
      setHost(null);
    };
  },[location.pathname]);

  useEffect(()=>{
    if(host)load();
  },[host,load]);

  if(!host)return null;

  return createPortal(
    <section className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/70 px-5 py-4 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-8">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-bold tracking-wide text-emerald-800 uppercase">
            <Building2 className="w-4 h-4"/> Şirket Toplam Varlık
          </div>
          <div className="mt-1 text-3xl font-bold text-emerald-950 tabular-nums">
            {loading && !data ? 'Hesaplanıyor…' : money(data?.company_total_assets_try)}
          </div>
          <div className="mt-1 text-xs text-emerald-700">Canlı stok alış değeri + demirbaş toplam değeri</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:min-w-[460px]">
          <div className="rounded-xl border border-emerald-200 bg-white/85 px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-medium text-emerald-700"><Boxes className="w-4 h-4"/> Depo Stok Değeri</div>
            <div className="mt-1 text-lg font-bold text-emerald-950 tabular-nums">{money(data?.stock_value_try)}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white/85 px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-medium text-emerald-700"><WalletCards className="w-4 h-4"/> Demirbaş Değeri</div>
            <div className="mt-1 text-lg font-bold text-emerald-950 tabular-nums">{money(data?.fixed_assets_value_try)}</div>
          </div>
        </div>

        <button type="button" onClick={load} disabled={loading} title="Şirket varlığını yenile" className="h-10 w-10 shrink-0 rounded-xl border border-emerald-200 bg-white inline-flex items-center justify-center text-emerald-800 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/>
        </button>
      </div>
    </section>,
    host
  );
}
